import React, { useState, useCallback } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Form, { Field, FormFooter, ErrorMessage } from '@atlaskit/form';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { Flex, Grid } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';

import { commitSuggestionModalMessages as messages } from './code-suggestions.i18n';

type Props = {
  closeModal: () => void;
  onConfirm: (message: string) => void;
  loading: boolean;
  filePath?: string | null | undefined;
  publishCancelBtnClickEvt: () => void;
  publishCommitBtnClickEvt: () => void;
};

export function CommitSuggestionModal({
  closeModal,
  onConfirm,
  loading,
  filePath = 'file',
  publishCancelBtnClickEvt,
  publishCommitBtnClickEvt,
}: Props): JSX.Element {
  const [isMessageValid, setIsMessageValid] = useState<boolean>(true);
  const { formatMessage } = useIntl();

  const handleSubmit = useCallback(
    (formState: { commitMessage: string; optionalDetails: string }) => {
      publishCommitBtnClickEvt();

      // don't allow submitting if there's no commit message
      if (!isMessageValid) {
        return;
      }

      const mainMessage: string = formState.commitMessage.trim();
      const details: string = formState.optionalDetails.trim();

      // if there's additional details message add the text to the commit message
      const commitMsg = `${mainMessage}${
        details.length ? `\n\n${details}` : ''
      }`;

      onConfirm(commitMsg);
    },
    [isMessageValid, onConfirm, publishCommitBtnClickEvt]
  );

  function onClickCancelBtn() {
    publishCancelBtnClickEvt();
    closeModal();
  }

  const validateMessage = useCallback(
    (val: string): string | undefined => {
      if (isMessageValid && !val) {
        // only sets error if error doesn't exists
        setIsMessageValid(false);
        return formatMessage(messages.fieldErrorText);
      } else if (!isMessageValid && val.length > 0) {
        // only remove error if it exists
        setIsMessageValid(true);
      }

      return undefined;
    },
    [isMessageValid, formatMessage]
  );

  return (
    <Modal onClose={closeModal}>
      <Form onSubmit={handleSubmit}>
        {({ formProps }) => (
          <form {...formProps} name="commit-suggestion-dialog">
            <ModalHeader>
              <Grid gap="space.200">
                <Flex justifyContent="start">
                  <ModalTitle>
                    <FormattedMessage {...messages.commitModalTitle} />
                  </ModalTitle>
                </Flex>
              </Grid>
            </ModalHeader>
            <ModalBody>
              <Field
                label={formatMessage(messages.commitMessageFieldLabel)}
                isRequired
                name="commitMessage"
                validate={validateMessage}
                defaultValue={formatMessage(messages.defaultCommitMessage, {
                  filePath,
                })}
              >
                {({ fieldProps }: any) => (
                  <>
                    <Textfield testId="commitMessageField" {...fieldProps} />
                    {!isMessageValid && (
                      <ErrorMessage>
                        <FormattedMessage {...messages.fieldErrorText} />
                      </ErrorMessage>
                    )}
                  </>
                )}
              </Field>
              {/* this will just get appended to the end of the commit message */}
              <Field label="" name="optionalDetails" defaultValue="">
                {({ fieldProps }: any) => (
                  <TextArea
                    placeholder={formatMessage(
                      messages.commitDetailsPlaceholderText
                    )}
                    testId="optionalDetailsField"
                    {...fieldProps}
                  />
                )}
              </Field>
            </ModalBody>
            <ModalFooter>
              <FormFooter>
                <Button
                  appearance="subtle"
                  onClick={onClickCancelBtn}
                  isDisabled={loading}
                >
                  <FormattedMessage {...messages.cancelButton} />
                </Button>
                <Button
                  type="submit"
                  isDisabled={!isMessageValid || loading}
                  isLoading={loading}
                  appearance="primary"
                >
                  <FormattedMessage {...messages.commitChangesButton} />
                </Button>
              </FormFooter>
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
}
